<template>
  <main class="er-root" :class="{ 'er-root--overlay': overlay }" :style="{ '--accent': page.accent, '--accent-soft': page.soft }">
    <div class="er-grain" aria-hidden="true"></div>
    <div class="er-orb er-orb--one" aria-hidden="true"></div>
    <div class="er-orb er-orb--two" aria-hidden="true"></div>
    <div class="er-frame" aria-hidden="true">
      <span class="er-corner er-corner--tl"></span>
      <span class="er-corner er-corner--tr"></span>
      <span class="er-corner er-corner--bl"></span>
      <span class="er-corner er-corner--br"></span>
    </div>

    <header class="er-top">
      <router-link to="/" class="er-brand">
        <span class="er-brand-mark"><img src="../../assets/Images/ecoframe_logo.png" alt="ECOFRAME" /></span>
        <span class="er-brand-text">
          <span class="er-brand-name">ECOFRAME</span>
          <span class="er-brand-sub">Kerala Forest Department</span>
        </span>
      </router-link>
    </header>

    <section class="er-body" role="alert">
      <div class="er-strip" aria-hidden="true"></div>

      <div class="er-icon" aria-hidden="true"><v-icon size="40">{{ page.icon }}</v-icon></div>

      <div class="er-kicker">{{ page.kicker }}</div>
      <div class="er-code" :aria-label="'Error ' + codeText">
        <span v-for="(d, i) in codeText.split('')" :key="i" class="er-digit" :style="{ animationDelay: i * 120 + 'ms' }">{{ d }}</span>
      </div>
      <h1 class="er-title">{{ page.title }}</h1>
      <p class="er-message">{{ page.message }}</p>

      <div v-if="showPath" class="er-path">
        <v-icon x-small>mdi-link-variant</v-icon>
        <span>{{ requested }}</span>
      </div>

      <div class="er-actions">
        <button type="button" class="er-btn er-btn--primary" @click="primary.run">
          <v-icon small>{{ primary.icon }}</v-icon>
          {{ primary.label }}
        </button>
        <button v-for="a in secondary" :key="a.label" type="button" class="er-btn er-btn--ghost" @click="a.run">
          <v-icon small>{{ a.icon }}</v-icon>
          {{ a.label }}
        </button>
      </div>

      <p class="er-help">{{ page.help }}</p>
    </section>

    <footer class="er-foot">
      © {{ year }} Kerala Forest Department · Developed by <strong>Leopard Tech Labs Pvt. Ltd.</strong>
    </footer>
  </main>
</template>

<script>
import { homeRouteFor } from "@/components/Common/navConfig";

// What each error says and which colour it wears
const PAGES = {
  400: {
    icon: "mdi-alert-octagon-outline",
    kicker: "Bad request",
    title: "That request didn't look right",
    message: "Something in the request couldn't be understood. Go back, check what you entered and try again.",
    help: "If it keeps happening, contact the support desk with the steps you followed.",
    accent: "#f59e0b",
    soft: "rgba(245, 158, 11, 0.16)",
  },
  401: {
    icon: "mdi-lock-clock",
    kicker: "Sign-in required",
    title: "Please sign in to continue",
    message: "Your session has ended, or you haven't signed in yet. Sign in again to carry on where you left off.",
    help: "For your security, sessions end after a period of inactivity.",
    accent: "#38bdf8",
    soft: "rgba(56, 189, 248, 0.16)",
  },
  403: {
    icon: "mdi-shield-lock-outline",
    kicker: "Access restricted",
    title: "This area isn't open to your account",
    message: "You're signed in, but your role doesn't have permission to open this page.",
    help: "If you think you should have access, ask your administrator.",
    accent: "#f97316",
    soft: "rgba(249, 115, 22, 0.16)",
  },
  404: {
    icon: "mdi-filmstrip-off",
    kicker: "Scene not found",
    title: "We couldn't find that page",
    message: "The page may have moved, been renamed, or the address may have a typo. Let's get you back on set.",
    help: "Use the buttons below, or the menu to find what you were looking for.",
    accent: "#4ade80",
    soft: "rgba(74, 222, 128, 0.16)",
  },
  408: {
    icon: "mdi-timer-sand",
    kicker: "Request timed out",
    title: "That took too long",
    message: "The server didn't answer in time. Check your connection and try again.",
    help: "Your work isn't lost: nothing was changed by this request.",
    accent: "#facc15",
    soft: "rgba(250, 204, 21, 0.16)",
  },
  429: {
    icon: "mdi-speedometer",
    kicker: "Too many requests",
    title: "Please slow down a little",
    message: "There have been too many requests in a short time. Wait a minute and try again.",
    help: "This limit protects the portal for everyone.",
    accent: "#facc15",
    soft: "rgba(250, 204, 21, 0.16)",
  },
  500: {
    icon: "mdi-server-off",
    kicker: "Server error",
    title: "Something went wrong on our side",
    message: "The server hit an unexpected problem while handling your request. It isn't anything you did.",
    help: "Try again in a moment. If it continues, please let the support desk know.",
    accent: "#f87171",
    soft: "rgba(248, 113, 113, 0.16)",
  },
  502: {
    icon: "mdi-cloud-off-outline",
    kicker: "Bad gateway",
    title: "The portal couldn't be reached",
    message: "A service the portal depends on isn't answering right now. This is usually brief.",
    help: "Try again in a minute.",
    accent: "#f87171",
    soft: "rgba(248, 113, 113, 0.16)",
  },
  503: {
    icon: "mdi-wrench-outline",
    kicker: "Temporarily unavailable",
    title: "We'll be right back",
    message: "The portal is down for maintenance or is very busy. Please try again shortly.",
    help: "Applications already submitted are safe.",
    accent: "#a78bfa",
    soft: "rgba(167, 139, 250, 0.16)",
  },
};

// Full-page error screen (401, 403, 404, 500 ...) in the look of the sign-in
// pages. `code` picks the wording; /error/:code shows any of them. With
// `overlay` it covers the page it is placed in.
export default {
  name: "ErrorPage",
  props: {
    code: { type: [String, Number], default: "" },
    overlay: { type: Boolean, default: false },
  },
  computed: {
    codeText() {
      const c = String(this.code || this.$route.params.code || "404");
      return PAGES[c] ? c : "404";
    },
    page() {
      return PAGES[this.codeText];
    },
    year() {
      return new Date().getFullYear();
    },
    loggedIn() {
      return !!this.$store && !!this.$store.state.isLoggedIn;
    },
    // Where "home" is for this visitor
    home() {
      return this.loggedIn ? homeRouteFor(localStorage.getItem("uType")) : "/";
    },
    requested() {
      return this.$route.fullPath;
    },
    showPath() {
      return (this.codeText === "404" || this.codeText === "403") && this.requested && this.requested.length > 1;
    },
    retryable() {
      return ["408", "429", "500", "502", "503"].includes(this.codeText);
    },
    primary() {
      if (this.codeText === "401") return { label: "Sign in", icon: "mdi-login", run: () => this.go("/login") };
      if (this.retryable) return { label: "Try again", icon: "mdi-refresh", run: () => window.location.reload() };
      return this.loggedIn
        ? { label: "Go to my dashboard", icon: "mdi-view-dashboard-outline", run: () => this.go(this.home) }
        : { label: "Go to the home page", icon: "mdi-home-outline", run: () => this.go("/") };
    },
    secondary() {
      const list = [];
      if (window.history.length > 1) list.push({ label: "Go back", icon: "mdi-arrow-left", run: () => this.$router.go(-1) });
      if (this.primary.label === "Try again" || this.codeText === "401") {
        list.push({ label: this.loggedIn && this.codeText !== "401" ? "Go to my dashboard" : "Home page", icon: "mdi-home-outline", run: () => this.go(this.codeText === "401" ? "/" : this.home) });
      }
      return list;
    },
  },
  methods: {
    go(path) {
      this.$router.push(path).catch(() => {});
    },
  },
};
</script>

<style scoped>

.er-root {
  --bg: #03100a;
  --ink: #e9f5ee;
  --muted: #9db3a6;
  --line: rgba(233, 245, 238, 0.14);
  --gold: #d6b97b;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
  font-family: "Inter", sans-serif;
  color: var(--ink);
  background:
    radial-gradient(ellipse at 50% -10%, rgba(21, 128, 61, 0.35), transparent 55%),
    linear-gradient(160deg, #03100a 0%, #062414 55%, #0a3a20 100%);
  -webkit-font-smoothing: antialiased;
}

.er-root--overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  overflow-y: auto;
}

.er-root a {
  text-decoration: none;
}

.er-grain {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  opacity: 0.07;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
}

.er-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
}

.er-orb--one {
  top: -140px;
  right: -100px;
  width: 440px;
  height: 440px;
  background: radial-gradient(circle, var(--accent-soft) 0%, transparent 70%);
  animation: er-drift 16s ease-in-out infinite alternate;
}

.er-orb--two {
  bottom: -160px;
  left: -120px;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(214, 185, 123, 0.16) 0%, transparent 70%);
  animation: er-drift 20s ease-in-out infinite alternate-reverse;
}

@keyframes er-drift {
  to {
    transform: translate(30px, 24px);
  }
}

/* Viewfinder corners */
.er-frame {
  position: absolute;
  inset: 22px;
  z-index: 1;
  pointer-events: none;
  opacity: 0.7;
}

.er-corner {
  position: absolute;
  width: 26px;
  height: 26px;
  border: 0 solid rgba(233, 245, 238, 0.5);
}

.er-corner--tl { top: 0; left: 0; border-top-width: 1px; border-left-width: 1px; }
.er-corner--tr { top: 0; right: 0; border-top-width: 1px; border-right-width: 1px; }
.er-corner--bl { bottom: 0; left: 0; border-bottom-width: 1px; border-left-width: 1px; }
.er-corner--br { bottom: 0; right: 0; border-bottom-width: 1px; border-right-width: 1px; }

.er-top {
  position: relative;
  z-index: 2;
  padding: 38px clamp(28px, 5vw, 64px) 0;
}

.er-brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.er-brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 0 0 1px rgba(214, 185, 123, 0.45), 0 8px 20px rgba(0, 0, 0, 0.35);
}

.er-brand-mark img {
  width: 86%;
  height: 86%;
  object-fit: contain;
}

.er-brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.er-brand-name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 4px;
  color: var(--ink);
}

.er-brand-sub {
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: rgba(233, 245, 238, 0.7);
}

.er-body {
  position: relative;
  z-index: 2;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 680px;
  margin: 0 auto;
  padding: 32px 24px 24px;
  text-align: center;
}

.er-body > * {
  animation: er-rise 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.er-body > *:nth-child(3) { animation-delay: 0.08s; }
.er-body > *:nth-child(4) { animation-delay: 0.12s; }
.er-body > *:nth-child(5) { animation-delay: 0.2s; }
.er-body > *:nth-child(6) { animation-delay: 0.26s; }
.er-body > *:nth-child(7) { animation-delay: 0.3s; }
.er-body > *:nth-child(8) { animation-delay: 0.36s; }
.er-body > *:nth-child(9) { animation-delay: 0.42s; }

@keyframes er-rise {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* Film-strip edge above the icon */
.er-strip {
  width: 150px;
  height: 8px;
  margin-bottom: 26px;
  border-radius: 4px;
  background: repeating-linear-gradient(115deg, rgba(233, 245, 238, 0.85) 0 10px, transparent 10px 20px);
  opacity: 0.55;
  animation: er-rise 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) both, er-film 5s linear 1s infinite;
  background-size: 200% 100%;
}

@keyframes er-film {
  to {
    background-position: 40px 0;
  }
}

.er-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  margin-bottom: 18px;
  border-radius: 26px;
  background: var(--accent-soft);
  border: 1px solid var(--accent);
  box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.03), 0 18px 40px rgba(0, 0, 0, 0.35);
  animation: er-rise 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) both, er-float 5s ease-in-out 1s infinite;
}

.er-icon .v-icon {
  color: var(--accent) !important;
}

@keyframes er-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

.er-kicker {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--gold);
}

.er-code {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 6px;
  font-family: "Inter", sans-serif;
  font-size: clamp(88px, 18vw, 168px);
  font-weight: 500;
  line-height: 1;
  letter-spacing: -2px;
  font-variant-numeric: lining-nums;
  background: linear-gradient(180deg, #ffffff 0%, var(--accent) 120%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.er-digit {
  display: inline-block;
  animation: er-pop 0.7s cubic-bezier(0.2, 1.3, 0.4, 1) both;
}

@keyframes er-pop {
  from {
    opacity: 0;
    transform: translateY(26px) scale(0.85);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.er-title {
  margin: 0 0 12px;
  font-size: clamp(22px, 3.6vw, 30px);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.3px;
  color: #f4faf6;
}

.er-message {
  max-width: 520px;
  margin: 0 0 18px;
  font-size: 16px;
  font-weight: 300;
  line-height: 1.7;
  color: rgba(233, 245, 238, 0.82);
}

.er-path {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  margin-bottom: 22px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.06);
  font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
  font-size: 12.5px;
  color: var(--muted);
}

.er-path .v-icon {
  color: inherit !important;
}

.er-path span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.er-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 4px;
}

.er-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 50px;
  padding: 0 26px;
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.2px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.er-btn .v-icon {
  color: inherit !important;
}

.er-btn:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 3px;
}

.er-btn--primary {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 50%, #16a34a 100%);
  color: #03140a;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 14px 34px rgba(34, 197, 94, 0.38);
}

.er-btn--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 20px 44px rgba(34, 197, 94, 0.5);
}

.er-btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--line);
  color: var(--ink);
  font-weight: 600;
}

.er-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(214, 185, 123, 0.7);
}

.er-help {
  margin: 26px 0 0;
  font-size: 13px;
  color: var(--muted);
}

.er-foot {
  position: relative;
  z-index: 2;
  padding: 0 20px 28px;
  text-align: center;
  font-size: 12px;
  color: rgba(157, 179, 166, 0.65);
}

.er-foot strong {
  font-weight: 500;
  color: var(--muted);
}

@media (max-width: 600px) {
  .er-top {
    padding: 30px 22px 0;
  }

  .er-btn {
    width: 100%;
  }

  .er-actions {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .er-body > *,
  .er-strip,
  .er-icon,
  .er-digit,
  .er-orb {
    animation: none !important;
  }
}
</style>
